import { cn } from '@shared/lib/cn';

import { CHART_SIZE, SERIES_STROKE_CLASS } from './chart.constant';
import type { DatavizSeries } from './model/dataviz.types';
import type { SparklinePoint } from './model/sparkline.types';
import { sparklinePath } from './sparkline.lib';
import { SPARKLINE_TEXT } from './sparkline.text';

export interface SparklineProps {
  points: SparklinePoint[];
  /** 기본 80 */
  width?: number;
  /** 기본 50 */
  height?: number;
  /** 선 색. 기본 1 */
  series?: DatavizSeries;
  /** 무엇의 추이인지(예: "매출"). 읽어 줄 이름이 "매출 추이: 최근 n기간" 이 된다 */
  subject?: string;
  /** 읽어 줄 이름을 통째로 바꿀 때. 기본은 `subject` 로 만든 "… 추이: 최근 n기간" · "… 추이: 자료 없음" */
  'aria-label'?: string;
  className?: string;
}

/**
 * 표 칸 안의 작은 추세선(포트폴리오 목록의 매출 · 기업가치). 결정 DD-22 2 — 빈 상자(DD-11)의 예외로 실제로 그린다.
 * 기존 서비스 `components/ui/LineGraph.tsx` · Figma `Sparkline`(2045:23)과 같다: 80×50, 선 `dataviz.series.1` 0.5px,
 * 면 · 점 · 축 없음. 자료가 없으면 기존처럼 빈 칸(같은 크기의 빈 자리, 보조기기는 "자료 없음")을 둔다.
 */
export function Sparkline({
  points,
  width = CHART_SIZE.sparklineWidth,
  height = CHART_SIZE.sparklineHeight,
  series = 1,
  subject,
  'aria-label': ariaLabel,
  className,
}: SparklineProps) {
  const path = sparklinePath(points, width, height);
  const count = path === '' ? 0 : path.split(' ').length;
  const label =
    ariaLabel ?? (count > 0 ? SPARKLINE_TEXT.label(count, subject) : SPARKLINE_TEXT.empty(subject));
  return (
    <svg
      role="img"
      aria-label={label}
      data-chart="sparkline"
      data-empty={count === 0 || undefined}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={cn('block shrink-0', className)}
    >
      {count > 0 ? (
        <polyline
          points={path}
          fill="none"
          strokeWidth={0.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          className={SERIES_STROKE_CLASS[series]}
        />
      ) : null}
    </svg>
  );
}
